/**
 * SSE (Server-Sent Events) hook for real-time agent token streaming.
 *
 * Provides a React hook that connects to the backend SSE endpoint
 * and handles token streaming, tool call events, reconnection,
 * and fallback to polling if SSE fails.
 */

import { useCallback, useEffect, useRef, useState } from "react";

// SSE event types matching backend SSEEventType
export type SSEEventType =
  | "start"
  | "token"
  | "tool_start"
  | "tool_end"
  | "step"
  | "done"
  | "error"
  | "context_compression";

export interface SSEStartData {
  message_id: string;
  status: string;
}

export interface SSETokenData {
  content: string;
  message_id: string;
}

export interface SSEToolStartData {
  tool_name: string;
  tool_input: Record<string, unknown>;
  message_id: string;
  tool_call_id?: string | null;
}

export interface SSEToolEndData {
  tool_name: string;
  tool_output: unknown;
  message_id: string;
  tool_call_id?: string | null;
}

export interface SSEStepData {
  steps: Array<Record<string, unknown>>;
  message_id: string;
}

export interface SSEDoneData {
  message_id: string;
  final_content?: string;
  message_processing_status: "PROCESSING" | "SUCCESS" | "ERROR";
  task_outcome?: "answer" | "explore_with_context" | "clarify" | "act_from_context" | "safe_limit";
}

export interface SSEErrorData {
  message_id: string;
  error: string;
  error_type?: string;
}

export interface SSEContextCompressionData {
  message_id: string;
  original_tokens: number;
  compressed_tokens: number;
  messages_summarized: number;
  messages_kept: number;
  summary_preview: string;
}

export type SSEEventData =
  | SSEStartData
  | SSETokenData
  | SSEToolStartData
  | SSEToolEndData
  | SSEStepData
  | SSEDoneData
  | SSEErrorData
  | SSEContextCompressionData;

export interface SSEStreamCallbacks {
  onStart?: (data: SSEStartData) => void;
  onToken?: (data: SSETokenData) => void;
  onToolStart?: (data: SSEToolStartData) => void;
  onToolEnd?: (data: SSEToolEndData) => void;
  onStep?: (data: SSEStepData) => void;
  onDone?: (data: SSEDoneData) => void;
  onError?: (data: SSEErrorData) => void;
  onContextCompression?: (data: SSEContextCompressionData) => void;
  onConnectionError?: (error: Error) => void;
  onRateLimited?: () => void;
}

export interface SSEStreamOptions {
  baseUrl: string;
  headers?: Record<string, string>;
  /** Async function that returns headers at call time (e.g. for fresh auth tokens).
   *  Merged after static `headers`, so it can override them. */
  getHeaders?: () => Promise<Record<string, string>>;
  callbacks: SSEStreamCallbacks;
  maxRetries?: number;
  retryDelay?: number;
  stallTimeout?: number;
}

export interface SSEStreamState {
  isStreaming: boolean;
  isThinking: boolean;
  isPreparing: boolean; // True after tokens received, before tool_start or done
  streamedContent: string;
  messageId: string | null;
  error: string | null;
}

export function useSSEStream(options: SSEStreamOptions) {
  const {
    baseUrl,
    headers = {},
    getHeaders,
    callbacks,
    maxRetries = 3,
    retryDelay = 1000,
    stallTimeout = 120000,
  } = options;

  const getHeadersRef = useRef(getHeaders);
  getHeadersRef.current = getHeaders;

  const [state, setState] = useState<SSEStreamState>({
    isStreaming: false,
    isThinking: false,
    isPreparing: false,
    streamedContent: "",
    messageId: null,
    error: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const lastEventIdRef = useRef<string | null>(null);
  const retryCountRef = useRef(0);
  const callbacksRef = useRef(callbacks);
  const unmountedRef = useRef(false);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Token batching for smoother rendering (batch every 50ms instead of every token)
  const tokenBufferRef = useRef<string>("");
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const TOKEN_BATCH_INTERVAL = 50; // ms

  // Update callbacks ref when callbacks change
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  const clearStallTimer = useCallback(() => {
    if (stallTimerRef.current) {
      clearTimeout(stallTimerRef.current);
      stallTimerRef.current = null;
    }
  }, []);

  const resetStallTimer = useCallback(() => {
    clearStallTimer();
    stallTimerRef.current = setTimeout(() => {
      // No data received within stallTimeout — connection may be dead
      console.warn(`SSE stream stalled for ${stallTimeout}ms, aborting`);
      abortControllerRef.current?.abort();
      setState((prev) => ({
        ...prev,
        isStreaming: false,
        error: "Stream stalled — no data received",
      }));
      callbacksRef.current.onConnectionError?.(
        new Error("SSE stream stalled — no data received")
      );
    }, stallTimeout);
  }, [clearStallTimer, stallTimeout]);

  // Flush accumulated tokens to state (for batching)
  const flushTokenBuffer = useCallback(() => {
    if (tokenBufferRef.current) {
      const tokens = tokenBufferRef.current;
      tokenBufferRef.current = "";
      setState((prev) => ({
        ...prev,
        isThinking: false,
        isPreparing: true,
        streamedContent: prev.streamedContent + tokens,
      }));
    }
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  // Schedule a flush if not already scheduled
  const scheduleFlush = useCallback(() => {
    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flushTokenBuffer, TOKEN_BATCH_INTERVAL);
    }
  }, [flushTokenBuffer]);

  const startStream = useCallback(
    async (body: Record<string, unknown>) => {
      // Cancel reader before aborting controller to avoid unhandled errors
      if (readerRef.current) {
        try {
          readerRef.current.cancel();
        } catch {
          // ignore
        }
        readerRef.current = null;
      }
      abortControllerRef.current?.abort();

      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Clear token buffer for new stream
      tokenBufferRef.current = "";
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }

      setState({
        isStreaming: true,
        isThinking: true,
        isPreparing: false,
        streamedContent: "",
        messageId: null,
        error: null,
      });

      try {
        // Resolve dynamic headers (e.g. fresh auth token) on every attempt
        const dynamicHeaders = getHeadersRef.current
          ? await getHeadersRef.current()
          : {};

        const reqHeaders: Record<string, string> = {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          ...headers,
          ...dynamicHeaders,
        };

        // Include Last-Event-ID for reconnection
        if (lastEventIdRef.current) {
          reqHeaders["Last-Event-ID"] = lastEventIdRef.current;
        }

        const response = await fetch(`${baseUrl}/api/agents/wayak/stream`, {
          method: "POST",
          headers: reqHeaders,
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        // Handle rate limiting
        if (response.status === 429) {
          const retryAfter = response.headers.get("Retry-After");
          const waitMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : 5000;
          setState((prev) => ({
            ...prev,
            isStreaming: false,
            error: "Rate limited. Try again shortly.",
          }));
          callbacksRef.current.onRateLimited?.();
          // Auto-retry after backoff if retries remaining
          if (retryCountRef.current < maxRetries) {
            retryCountRef.current += 1;
            retryTimeoutRef.current = setTimeout(() => {
              if (!unmountedRef.current) {
                startStream(body);
              }
            }, waitMs);
          }
          return;
        }

        if (!response.ok) {
          throw new Error(`SSE request failed: ${response.status} ${response.statusText}`);
        }

        // Extract message ID from header
        const messageId = response.headers.get("X-Message-Id");
        if (messageId) {
          setState((prev) => ({ ...prev, messageId }));
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("Response body is not readable");
        }
        readerRef.current = reader;

        const decoder = new TextDecoder();
        let buffer = "";
        // Tracks whether the backend sent a terminal frame before the stream
        // closed. If reader.read() returns done without ever seeing a `done`
        // or `error` event (proxy timeout, network drop, worker restart), we
        // treat that as a recoverable failure and force the consumer to
        // rehydrate from the DB instead of showing a spinner forever.
        let sawTerminalEvent = false;

        // Start stall detection
        resetStallTimer();

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            // Reset stall timer on every chunk received (including keepalives)
            resetStallTimer();

            buffer += decoder.decode(value, { stream: true });

            // Parse SSE events from buffer
            const events = buffer.split("\n\n");
            // Keep incomplete event in buffer
            buffer = events.pop() || "";

            for (const eventBlock of events) {
              if (!eventBlock.trim()) continue;

              const lines = eventBlock.split("\n");
              let eventType: string | null = null;
              let eventData: string | null = null;
              let eventId: string | null = null;

              for (const line of lines) {
                if (line.startsWith("event: ")) {
                  eventType = line.slice(7);
                } else if (line.startsWith("data: ")) {
                  eventData = line.slice(6);
                } else if (line.startsWith("id: ")) {
                  eventId = line.slice(4);
                }
              }

              // Track last event ID for reconnection
              if (eventId) {
                lastEventIdRef.current = eventId;
              }

              if (!eventType || !eventData) continue;

              try {
                const parsed = JSON.parse(eventData);

                switch (eventType as SSEEventType) {
                  case "start":
                    // Agent started thinking - keep isThinking true
                    callbacksRef.current.onStart?.(parsed as SSEStartData);
                    break;

                  case "token":
                    // Batch tokens for smoother rendering
                    tokenBufferRef.current += parsed.content;
                    scheduleFlush();
                    callbacksRef.current.onToken?.(parsed as SSETokenData);
                    break;

                  case "tool_start":
                    // Tool started - no longer thinking or preparing
                    setState((prev) => ({ ...prev, isThinking: false, isPreparing: false }));
                    callbacksRef.current.onToolStart?.(parsed as SSEToolStartData);
                    break;

                  case "tool_end":
                    callbacksRef.current.onToolEnd?.(parsed as SSEToolEndData);
                    break;

                  case "step":
                    callbacksRef.current.onStep?.(parsed as SSEStepData);
                    break;

                  case "done":
                    sawTerminalEvent = true;
                    // Flush any remaining buffered tokens
                    flushTokenBuffer();
                    setState((prev) => ({
                      ...prev,
                      isStreaming: false,
                      isThinking: false,
                      isPreparing: false,
                    }));
                    retryCountRef.current = 0;
                    lastEventIdRef.current = null;
                    clearStallTimer();
                    callbacksRef.current.onDone?.(parsed as SSEDoneData);
                    break;

                  case "context_compression":
                    callbacksRef.current.onContextCompression?.(parsed as SSEContextCompressionData);
                    break;

                  case "error":
                    sawTerminalEvent = true;
                    // Flush any remaining buffered tokens
                    flushTokenBuffer();
                    setState((prev) => ({
                      ...prev,
                      isStreaming: false,
                      isThinking: false,
                      isPreparing: false,
                      error: parsed.error,
                    }));
                    clearStallTimer();
                    lastEventIdRef.current = null; // Clear to prevent stale ID on retry
                    callbacksRef.current.onError?.(parsed as SSEErrorData);
                    break;
                }
              } catch (parseError) {
                console.warn("Failed to parse SSE event data:", parseError);
              }
            }
          }
        } finally {
          reader.releaseLock();
          readerRef.current = null;
        }

        // Stream reader finished. If we never saw a `done` or `error` frame,
        // the backend terminated the connection without a terminal event
        // (proxy timeout, dropped tunnel, worker restart). Flush pending
        // tokens and hand off to onConnectionError so the consumer can
        // rehydrate from the DB — otherwise the UI sits on a spinner
        // forever.
        flushTokenBuffer();
        clearStallTimer();
        setState((prev) => ({
          ...prev,
          isStreaming: false,
          isThinking: false,
          isPreparing: false,
        }));
        if (!sawTerminalEvent && !controller.signal.aborted) {
          lastEventIdRef.current = null;
          callbacksRef.current.onConnectionError?.(
            new Error("SSE stream ended without a done or error event")
          );
        }
      } catch (error) {
        clearStallTimer();
        if (controller.signal.aborted) return;

        const err = error instanceof Error ? error : new Error(String(error));

        // Retry on connection errors
        if (retryCountRef.current < maxRetries) {
          retryCountRef.current += 1;
          console.warn(
            `SSE connection failed (attempt ${retryCountRef.current}/${maxRetries}), retrying...`
          );
          const backoffMs = Math.min(
            retryDelay * Math.pow(2, retryCountRef.current - 1) + Math.random() * 1000,
            30000
          );
          retryTimeoutRef.current = setTimeout(() => {
            // Check if unmounted before retrying
            if (!unmountedRef.current && !abortControllerRef.current?.signal.aborted) {
              startStream(body);
            }
          }, backoffMs);
          return;
        }

        setState((prev) => ({
          ...prev,
          isStreaming: false,
          error: err.message,
        }));
        callbacksRef.current.onConnectionError?.(err);
      }
    },
    [baseUrl, headers, maxRetries, retryDelay, resetStallTimer, clearStallTimer, scheduleFlush, flushTokenBuffer]
  );

  const stopStream = useCallback(() => {
    clearStallTimer();
    // Flush any buffered tokens before stopping
    flushTokenBuffer();
    // Cancel reader before aborting to prevent unhandled errors
    if (readerRef.current) {
      try {
        readerRef.current.cancel();
      } catch {
        // ignore
      }
      readerRef.current = null;
    }
    abortControllerRef.current?.abort();
    setState((prev) => ({ ...prev, isStreaming: false, isThinking: false, isPreparing: false }));
    lastEventIdRef.current = null;
    retryCountRef.current = 0;
    tokenBufferRef.current = "";
    // Clear any pending retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, [clearStallTimer, flushTokenBuffer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      clearStallTimer();
      // Clear flush timer
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
      }
      if (readerRef.current) {
        try {
          readerRef.current.cancel();
        } catch {
          // ignore
        }
      }
      abortControllerRef.current?.abort();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [clearStallTimer]);

  return {
    ...state,
    startStream,
    stopStream,
  };
}
